import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { toast } from 'sonner';
import { 
  Calendar, Users, ClipboardCheck, Plus, Trash2, Edit2,
  CheckCircle, XCircle, AlertCircle, Loader2, Clock
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AttendanceManager = ({ schoolId, schoolName, students = [] }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [updating, setUpdating] = useState(false);
  
  // Teacher state
  const [isTeacher, setIsTeacher] = useState(false);
  const [assignedStudents, setAssignedStudents] = useState([]);
  const [teacherCheckDone, setTeacherCheckDone] = useState(false);
  
  // New session form
  const [showNewSession, setShowNewSession] = useState(false);
  const [newSession, setNewSession] = useState({
    session_date: new Date().toISOString().split('T')[0],
    session_title: 'Class Session',
    notes: ''
  });
  
  // Edit session
  const [showEditSession, setShowEditSession] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  
  // Attendance taking
  const [selectedSession, setSelectedSession] = useState(null);
  const [attendanceRecords, setAttendanceRecords] = useState({});
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);

  // Check if current user is a teacher and get their assigned students
  useEffect(() => {
    const checkTeacherStatus = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setTeacherCheckDone(true);
        return;
      }
      
      try {
        // Check if user is a teacher
        const checkRes = await fetch(`${BACKEND_URL}/api/teachers/check`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (checkRes.ok) {
          const checkData = await checkRes.json();
          
          if (checkData.is_teacher) {
            setIsTeacher(true);
            
            // Get assigned students for this school
            const studentsRes = await fetch(`${BACKEND_URL}/api/teachers/my-students?school_id=${schoolId}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (studentsRes.ok) {
              const studentsData = await studentsRes.json();
              setAssignedStudents(studentsData.students || []);
            }
          }
        }
      } catch (error) {
        console.error('Error checking teacher status:', error);
      } finally {
        setTeacherCheckDone(true);
      }
    };
    
    checkTeacherStatus();
  }, [schoolId]);

  const fetchSessions = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/schools/${schoolId}/attendance/sessions`);
      if (res.ok) {
        const data = await res.json();
        setSessions(data);
      }
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
    } finally {
      setLoading(false);
    }
  }, [schoolId]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  // Compute the list of students to show (filtered for teachers)
  const displayStudents = isTeacher && assignedStudents.length > 0 
    ? assignedStudents 
    : students;

  const handleCreateSession = async () => {
    if (!newSession.session_date) {
      toast.error('Please select a date');
      return;
    }

    setCreating(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/schools/${schoolId}/attendance/sessions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          school_id: schoolId,
          ...newSession
        })
      });

      if (!res.ok) throw new Error('Failed to create session');

      const data = await res.json();
      toast.success('Session created successfully');
      setShowNewSession(false);
      setNewSession({
        session_date: new Date().toISOString().split('T')[0],
        session_title: 'Class Session',
        notes: ''
      });
      fetchSessions();
      
      // Open attendance modal for the new session
      setSelectedSession(data.session);
      initializeAttendance();
      setShowAttendanceModal(true);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setCreating(false);
    }
  };

  const initializeAttendance = (existingRecords = []) => {
    const records = {};
    displayStudents.forEach(student => {
      const existing = existingRecords.find(r => r.student_id === student.email);
      records[student.email] = existing?.status || 'present';
    });
    setAttendanceRecords(records);
  };

  const handleOpenAttendance = async (session) => {
    setSelectedSession(session);
    
    // Fetch existing records for this session
    try {
      const res = await fetch(`${BACKEND_URL}/api/schools/${schoolId}/attendance/records?session_id=${session.id}`);
      if (res.ok) {
        const records = await res.json();
        initializeAttendance(records);
      } else {
        initializeAttendance([]);
      }
    } catch (error) {
      initializeAttendance([]);
    }
    
    setShowAttendanceModal(true);
  };

  const handleSubmitAttendance = async () => {
    if (!selectedSession) return;

    setSubmitting(true);
    try {
      const records = Object.entries(attendanceRecords).map(([studentId, status]) => ({
        student_id: studentId,
        status: status
      }));

      const res = await fetch(`${BACKEND_URL}/api/schools/${schoolId}/attendance/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          school_id: schoolId,
          session_id: selectedSession.id,
          records: records
        })
      });

      if (!res.ok) throw new Error('Failed to submit attendance');

      toast.success('Attendance submitted successfully');
      setShowAttendanceModal(false);
      fetchSessions();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteSession = async (sessionId) => {
    if (!window.confirm('Are you sure you want to delete this session and all its attendance records?')) {
      return;
    }

    try {
      const res = await fetch(`${BACKEND_URL}/api/schools/${schoolId}/attendance/sessions/${sessionId}`, {
        method: 'DELETE'
      });

      if (!res.ok) throw new Error('Failed to delete session');

      toast.success('Session deleted');
      fetchSessions();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleEditSession = (session) => {
    setEditingSession({
      ...session,
      session_date: session.session_date,
      session_title: session.session_title,
      notes: session.notes || ''
    });
    setShowEditSession(true);
  };

  const handleUpdateSession = async () => {
    if (!editingSession) return;

    setUpdating(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/schools/${schoolId}/attendance/sessions/${editingSession.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          session_date: editingSession.session_date,
          session_title: editingSession.session_title,
          notes: editingSession.notes
        })
      });

      if (!res.ok) throw new Error('Failed to update session');

      toast.success('Session updated successfully');
      setShowEditSession(false);
      setEditingSession(null);
      fetchSessions();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUpdating(false);
    }
  };

  const markAllPresent = () => {
    const records = {};
    displayStudents.forEach(student => {
      records[student.email] = 'present';
    });
    setAttendanceRecords(records);
  };

  const markAllAbsent = () => {
    const records = {};
    displayStudents.forEach(student => {
      records[student.email] = 'absent';
    });
    setAttendanceRecords(records);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'present':
        return <CheckCircle className="text-green-500" size={18} />;
      case 'absent':
        return <XCircle className="text-red-500" size={18} />;
      case 'excused':
        return <AlertCircle className="text-yellow-500" size={18} />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'present':
        return <Badge className="bg-green-500">Present</Badge>;
      case 'absent':
        return <Badge className="bg-red-500">Absent</Badge>;
      case 'excused':
        return <Badge className="bg-yellow-500">Excused</Badge>;
      default:
        return <Badge variant="secondary">Unknown</Badge>;
    }
  };

  return (
    <Card className="border-2 border-dashed border-blue-200 bg-gradient-to-br from-blue-50 to-cyan-50">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-blue-800">
              <ClipboardCheck className="text-blue-600" />
              Attendance Tracker
            </CardTitle>
            <CardDescription className="text-blue-600">
              Track student attendance for {schoolName}
            </CardDescription>
          </div>
          <Dialog open={showNewSession} onOpenChange={setShowNewSession}>
            <DialogTrigger asChild>
              <Button 
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="new-session-btn"
              >
                <Plus size={16} className="mr-2" /> New Session
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Calendar className="text-blue-600" />
                  Create New Attendance Session
                </DialogTitle>
                <DialogDescription>
                  Create a new class session to track attendance
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="session_date">Session Date *</Label>
                  <Input
                    id="session_date"
                    type="date"
                    value={newSession.session_date}
                    onChange={(e) => setNewSession(prev => ({ ...prev, session_date: e.target.value }))}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="session_title">Session Title</Label>
                  <Input
                    id="session_title"
                    placeholder="e.g., Week 1 - Introduction"
                    value={newSession.session_title}
                    onChange={(e) => setNewSession(prev => ({ ...prev, session_title: e.target.value }))}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="notes">Notes (optional)</Label>
                  <Input
                    id="notes"
                    placeholder="Any additional notes..."
                    value={newSession.notes}
                    onChange={(e) => setNewSession(prev => ({ ...prev, notes: e.target.value }))}
                  />
                </div>
                
                <div className="flex justify-end gap-3 pt-4">
                  <Button variant="outline" onClick={() => setShowNewSession(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleCreateSession}
                    disabled={creating || !newSession.session_date}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {creating ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creating...</>
                    ) : (
                      <><Plus size={16} className="mr-2" /> Create & Take Attendance</>
                    )}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      
      <CardContent>
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg p-4 border">
            <div className="flex items-center gap-2 text-blue-600 mb-1">
              <Calendar size={18} />
              <span className="font-medium">Total Sessions</span>
            </div>
            <p className="text-2xl font-bold text-gray-800">{sessions.length}</p>
          </div>
          
          <div className="bg-white rounded-lg p-4 border">
            <div className="flex items-center gap-2 text-green-600 mb-1">
              <Users size={18} />
              <span className="font-medium">{isTeacher ? 'Assigned Students' : 'Enrolled Students'}</span>
            </div>
            <p className="text-2xl font-bold text-gray-800">{displayStudents.length}</p>
            {isTeacher && (
              <p className="text-xs text-gray-500 mt-1">Assigned to you</p>
            )}
          </div>
          
          <div className="bg-white rounded-lg p-4 border">
            <div className="flex items-center gap-2 text-purple-600 mb-1">
              <ClipboardCheck size={18} />
              <span className="font-medium">Sessions Completed</span>
            </div>
            <p className="text-2xl font-bold text-gray-800">
              {sessions.filter(s => s.attendance_submitted).length}
            </p>
          </div>
        </div>

        {/* Sessions List */}
        {loading ? (
          <div className="text-center py-8">
            <Loader2 className="mx-auto animate-spin text-blue-500" size={32} />
            <p className="text-gray-500 mt-2">Loading sessions...</p>
          </div>
        ) : sessions.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Calendar size={48} className="mx-auto mb-4 text-gray-300" />
            <p>No attendance sessions yet</p>
            <p className="text-sm mt-2">Click &quot;New Session&quot; to create your first session</p>
          </div>
        ) : (
          <div className="space-y-3">
            <h4 className="font-medium text-gray-700">Recent Sessions</h4>
            <div className="max-h-64 overflow-y-auto space-y-2">
              {sessions.slice(0, 10).map((session) => (
                <div
                  key={session.id}
                  className="flex items-center justify-between p-3 bg-white rounded-lg border hover:border-blue-300 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-full ${session.attendance_submitted ? 'bg-green-100' : 'bg-yellow-100'}`}>
                      {session.attendance_submitted ? (
                        <CheckCircle className="text-green-600" size={20} />
                      ) : (
                        <Clock className="text-yellow-600" size={20} />
                      )}
                    </div>
                    <div>
                      <p className="font-medium">{session.session_title}</p>
                      <p className="text-sm text-gray-500">
                        {/* Parse date as local timezone by appending T00:00:00 */}
                        {new Date(session.session_date + 'T00:00:00').toLocaleDateString('en-US', {
                          weekday: 'short',
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={session.attendance_submitted ? "default" : "secondary"}>
                      {session.attendance_submitted ? 'Completed' : 'Pending'}
                    </Badge>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleOpenAttendance(session)}
                    >
                      {session.attendance_submitted ? 'View/Edit' : 'Take Attendance'}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-blue-500 hover:text-blue-700"
                      onClick={() => handleEditSession(session)}
                      title="Edit session details"
                    >
                      <Edit2 size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-500 hover:text-red-700"
                      onClick={() => handleDeleteSession(session.id)}
                      title="Delete session"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Attendance Modal */}
        <Dialog open={showAttendanceModal} onOpenChange={setShowAttendanceModal}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ClipboardCheck className="text-blue-600" />
                Take Attendance - {selectedSession?.session_title}
              </DialogTitle>
              <DialogDescription>
                {/* Parse date as local timezone by appending T00:00:00 */}
                {selectedSession && new Date(selectedSession.session_date + 'T00:00:00').toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 mt-4">
              {/* Quick Actions */}
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={markAllPresent} className="text-green-600">
                  <CheckCircle size={14} className="mr-1" /> Mark All Present
                </Button>
                <Button variant="outline" size="sm" onClick={markAllAbsent} className="text-red-600">
                  <XCircle size={14} className="mr-1" /> Mark All Absent
                </Button>
              </div>

              {/* Student List */}
              {displayStudents.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Users size={48} className="mx-auto mb-4 text-gray-300" />
                  <p>{isTeacher ? 'No students assigned to you for this school' : 'No students enrolled in this school'}</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Student</TableHead>
                      <TableHead>Branch</TableHead>
                      <TableHead className="text-center">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {displayStudents.map((student) => (
                      <TableRow key={student.email}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{student.name}</p>
                            <p className="text-sm text-gray-500">{student.email}</p>
                          </div>
                        </TableCell>
                        <TableCell>{student.branch || '-'}</TableCell>
                        <TableCell>
                          <div className="flex justify-center gap-2">
                            <Select
                              value={attendanceRecords[student.email] || 'present'}
                              onValueChange={(value) => setAttendanceRecords(prev => ({
                                ...prev,
                                [student.email]: value
                              }))}
                            >
                              <SelectTrigger className="w-32">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="present">
                                  <div className="flex items-center gap-2">
                                    <CheckCircle className="text-green-500" size={14} />
                                    Present
                                  </div>
                                </SelectItem>
                                <SelectItem value="absent">
                                  <div className="flex items-center gap-2">
                                    <XCircle className="text-red-500" size={14} />
                                    Absent
                                  </div>
                                </SelectItem>
                                <SelectItem value="excused">
                                  <div className="flex items-center gap-2">
                                    <AlertCircle className="text-yellow-500" size={14} />
                                    Excused
                                  </div>
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}

              {/* Summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium mb-2">Summary</h4>
                <div className="flex gap-4 text-sm">
                  <span className="flex items-center gap-1">
                    <CheckCircle className="text-green-500" size={16} />
                    Present: {Object.values(attendanceRecords).filter(s => s === 'present').length}
                  </span>
                  <span className="flex items-center gap-1">
                    <XCircle className="text-red-500" size={16} />
                    Absent: {Object.values(attendanceRecords).filter(s => s === 'absent').length}
                  </span>
                  <span className="flex items-center gap-1">
                    <AlertCircle className="text-yellow-500" size={16} />
                    Excused: {Object.values(attendanceRecords).filter(s => s === 'excused').length}
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowAttendanceModal(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmitAttendance}
                  disabled={submitting || displayStudents.length === 0}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="submit-attendance-btn"
                >
                  {submitting ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving...</>
                  ) : (
                    <><ClipboardCheck size={16} className="mr-2" /> Save Attendance</>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Session Modal */}
        <Dialog open={showEditSession} onOpenChange={setShowEditSession}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit2 className="text-blue-600" />
                Edit Session
              </DialogTitle>
              <DialogDescription>
                Update the session details
              </DialogDescription>
            </DialogHeader>
            
            {editingSession && (
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="edit_session_date">Session Date *</Label>
                  <Input
                    id="edit_session_date"
                    type="date"
                    value={editingSession.session_date}
                    onChange={(e) => setEditingSession(prev => ({ ...prev, session_date: e.target.value }))}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="edit_session_title">Session Title</Label>
                  <Input
                    id="edit_session_title"
                    placeholder="e.g., Week 1 - Introduction"
                    value={editingSession.session_title}
                    onChange={(e) => setEditingSession(prev => ({ ...prev, session_title: e.target.value }))}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="edit_notes">Notes (optional)</Label>
                  <Input
                    id="edit_notes"
                    placeholder="Any additional notes..."
                    value={editingSession.notes}
                    onChange={(e) => setEditingSession(prev => ({ ...prev, notes: e.target.value }))}
                  />
                </div>

                {editingSession.attendance_submitted && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                    <AlertCircle size={16} className="inline mr-2" />
                    This session has attendance records. Editing will only update session details, not attendance data.
                  </div>
                )}
                
                <div className="flex justify-end gap-3 pt-4">
                  <Button variant="outline" onClick={() => {
                    setShowEditSession(false);
                    setEditingSession(null);
                  }}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleUpdateSession}
                    disabled={updating || !editingSession.session_date}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {updating ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Updating...</>
                    ) : (
                      <><Edit2 size={16} className="mr-2" /> Update Session</>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default AttendanceManager;
